// packages
import { index } from 'd3-array';
import { json } from 'd3-fetch';
import { derived, writable } from 'svelte/store';

// local
import { getSummaryUrl, getOfficeRaceUrl } from '../constants';
import { indexByCode } from './commons';

// types
import type { Readable } from 'svelte/store';
import type { NationalInterface, OfficeRaceFeed } from '../interfaces';

export async function createNationalStore(
  seconds = 30
): Promise<Readable<NationalInterface>> {
  // build our URL
  const url = getSummaryUrl('2020-11-03');

  // our timeout interval
  const interval = seconds * 1000;

  // we need to track the active timeout
  let timeoutId: number | undefined;

  // we need to keep track of the last run
  let lastUpdated: number;

  // set up our request function
  async function makeRequest() {
    console.debug('Making request:', url);
    const raw = (await json<NationalInterface>(url)) as NationalInterface;

    lastUpdated = Date.now();
    return raw;
  }

  // get the initial data
  const feed = await makeRequest();

  // create our store
  const { subscribe } = writable(feed, function start(set) {
    async function run() {
      // make our request
      const feed = await makeRequest();

      // pass it along
      console.debug('Making update:', url);
      set(feed);

      timeoutId = setTimeout(run, interval);
    }

    const now = Date.now();

    if (now - lastUpdated < interval) {
      // we just had a run, so cool it
      timeoutId = setTimeout(run, interval);
    } else {
      // it's been a while, so get going!
      run();
    }

    return function stop() {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };
  });

  return { subscribe };
}

export async function createOfficeRaceFeed(
  office: string,
  seconds = 30
): Promise<Readable<OfficeRaceFeed>> {
  // build our URL
  const url = getOfficeRaceUrl('2020-11-03', office);

  // our timeout interval
  const interval = seconds * 1000;

  // we need to track the active timeout
  let timeoutId: number | undefined;

  // we need to keep track of the last run
  let lastUpdated: number;

  // set up our request function
  async function makeRequest() {
    console.debug('Making request:', url);
    const raw = (await json<OfficeRaceFeed>(url)) as OfficeRaceFeed;

    lastUpdated = Date.now();
    return raw;
  }

  // get the initial data
  const feed = await makeRequest();

  // create our store
  const { subscribe } = writable(feed, function start(set) {
    async function run() {
      // make our request
      const feed = await makeRequest();

      // pass it along
      console.debug('Making update:', url);
      set(feed);

      timeoutId = setTimeout(run, interval);
    }

    const now = Date.now();

    if (now - lastUpdated < interval) {
      // we just had a run, so cool it
      timeoutId = setTimeout(run, interval);
    } else {
      // it's been a while, so get going!
      run();
    }

    return function stop() {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };
  });

  return { subscribe };
}

export function createStateLookup(store: Readable<NationalInterface>) {
  return derived(store, ($feed) => index($feed.president.states, indexByCode));
}
